import React, { useState, useEffect } from 'react';
import { Route, createHashRouter, RouterProvider, createRoutesFromElements } from 'react-router-dom';

import { Grid } from '@mui/material';
import { GoogleReCaptchaProvider, GoogleReCaptcha } from 'react-google-recaptcha-v3';
import Footer from './container/Footer';
import ProgressBar from './container/ProgressBar';
import Landing from './component/Landing';
import Help from './component/Help'
import ReactPolling from 'react-polling';
import UnavailableBanner from './component/errorHandling/UnavailableBanner';
import ErrorBoundary from './component/errorHandling/ErrorBoundary';
import Confirmation from './component/Confirmation';
 
function App(props) {
  const [recaptchaKey, setRecaptchaKey] = useState(null);
  const [isStartDisabled, setisStartDisabled] = useState(false);
  const [formIsHalfFilledOut, setformIsHalfFilledOut] = useState(false);

  useEffect(() => {
    const dynamicKey = getRecapthcaSiteKeyFromProxy(); 
    // console.log("dynamic recaptcha site key " + dynamicKey)
    setRecaptchaKey(dynamicKey);
  }, [])
  
  const getRecapthcaSiteKeyFromProxy = () => {
    const hostname = window.location.hostname;

    // Using a map of hostnames to reCAPTCHA keys allows a single build artifact
    // to be deployed across multiple environments. These keys are public.
    const keyMap = {
      // Production
      'fincen105.cbp.dhs.gov': '6LcUk5AmAAAAAFytOdPbOpYkknNyGQVCO8gUn-u2',
      'fincen105.prod.mesh.cbp.dhs.gov': '6LcUk5AmAAAAAFytOdPbOpYkknNyGQVCO8gUn-u2',
      'tded-ecmir-ui.prod.mesh.cbp.dhs.gov': '6LcUk5AmAAAAAFytOdPbOpYkknNyGQVCO8gUn-u2',
      // Staging/SAT
      'fincen105.sat.mesh.cbp.dhs.gov': '6LfjeEolAAAAAJDYinxlmfT9wDkI15XXJYyZRLYB',
      'tded-ecmir-ui.sat.mesh.cbp.dhs.gov': '6LfjeEolAAAAAJDYinxlmfT9wDkI15XXJYyZRLYB',
      // Development
      'fincen105.dev.mesh.cbp.dhs.gov': '6LcfpgglAAAAACMwccqESjxhXn9MTanKlvRJHkKI',
      'tded-ecmir-ui.dev.mesh.cbp.dhs.gov': '6LcfpgglAAAAACMwccqESjxhXn9MTanKlvRJHkKI',
      // Local development
      'localhost': import.meta.env.VITE_RECAP_SITE_KEY,
    };

    if (keyMap[hostname]) {
      return keyMap[hostname];
    }

    // Fallback for other environments or if REACT_APP_RECAP_SITE_KEY is needed for testing.
    return import.meta.env.VITE_RECAP_SITE_KEY;
  };

  const reCaptchaCallback = (token) => {
    // console.log("reCaptchaCallback Node Environment: " + process.env.NODE_ENV)
    // Here you will get the final recaptchaToken!!!  
    // console.log(JSON.stringify(token), " <= recaptcha token")
    const request = {
      "event": {
        "token": token,
        "siteKey": getRecapthcaSiteKeyFromProxy(),
        "expectedAction": "ecmir_verify"
      }
    }

    // Define a safelist of fully qualified domain names (FQDNs) where this app is allowed to run.
    // This directly implements the vendor's recommendation on the client-side.
    const FQDN_SAFELIST = [
      'localhost', // For local development
      // Add your production and staging domains here. For example:
      'fincen105.cbp.dhs.gov',
      'fincen105.prod.mesh.cbp.dhs.gov',
      'fincen105.sat.mesh.cbp.dhs.gov',
      'fincen105.dev.mesh.cbp.dhs.gov',
      'tded-ecmir-ui.prod.mesh.cbp.dhs.gov',
      'tded-ecmir-ui.sat.mesh.cbp.dhs.gov',
      'tded-ecmir-ui.dev.mesh.cbp.dhs.gov',
    ];

    // Before making the fetch call, validate that the current hostname is in the safelist.
    // This prevents the client from being used to make requests from unexpected domains.
    if (!FQDN_SAFELIST.includes(window.location.hostname)) {
      console.error(`Request blocked: The current domain '${window.location.hostname}' is not in the FQDN safelist.`);
      // Here you could update the UI to inform the user of a configuration error.
      return;
    }

    fetch('/assessments', {
      method: "POST",
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'        
      },
      body: JSON.stringify(request)
    })
    .then(async response => {
      const data = await response.json();
      // console.log('response ' + JSON.stringify(data))
      console.log('recaptcha valid ' + data.tokenProperties.valid)
      console.log('recaptcha score ' + data.riskAnalysis.score)
    })
  }

  const formCompleted = (status) => {
    setformIsHalfFilledOut(status)
  }

  const onSuccess = e => {
    setisStartDisabled(false)
    // console.log("App - Success");
  }
  const onFailure = () => {
    setisStartDisabled(true)
    console.log("Application - Failure in connection with service") 
  }

  // Define routes using createRoutesFromElements (optional, can also be a plain array of objects)
  const router = createHashRouter(
    createRoutesFromElements(
      <>
        <Route path="/" Component={props => <Landing {...props} isStartDisabled={isStartDisabled} />} />
        <Route path='/form' Component={props => <ProgressBar {...props} formCompleted={formCompleted} formIsHalfFilledOut={formIsHalfFilledOut}/> }  />
        <Route path='/confirm' Component={props => <Confirmation {...props}/>} />
        <Route path="/help" Component={Help} />
      </>
    )
  )

  return (     
    <Grid
      container
      sx={{
        flexGrow: 1,
        maxWidth: "100%",
        justifyContent: "center",
        alignItems: "inherit",
      }}
      spacing={0}
      direction="row">
      
      {/* <Header /> */}
      
      <ReactPolling url= {'/provisionalCMIR/echo'} interval= {60000} retryCount={1} method={'GET'}
          onSuccess={() => { onSuccess(); return true;}} onFailure={() => {onFailure();}} 
          render={() => { return null; }}
      />
      
      {/* {console.log("App.js - isStartDisabled value: "+ this.state.isStartDisabled)} */}
      {isStartDisabled && <UnavailableBanner/> }
      
      <ErrorBoundary>
        <RouterProvider router={router} future={{ v7_startTransition: true }} />
        {/* <Routes>
          <Route path="/" Component={props => <Landing {...props} formCompleted={formCompleted} isStartDisabled={isStartDisabled} />} />
          <Route path='/form' Component={props => <ProgressBar {...props} formCompleted={formCompleted}/>} />
          <Route path='/confirm' Component={props => <Confirmation {...props}/>} />
          <Route path="/status" render={() => {window.location.href="status.html"}} />
          <Route path="/help" Component={Help} />
        </Routes> */}
      </ErrorBoundary>
      
      {/* <ErrorBoundary>
        <Routes>
          <Prompt
            when={formIsHalfFilledOut}              
            message="Are you sure you want to leave? All of the entered data will be lost."              
          />
          <Route exact path="/status" render={() => {window.location.href="status.html"}} />
          <Route exact path="/help" component={Help} />
          <Route exact path='/' render={props => <Landing {...props} formCompleted={this.formCompleted} isStartDisabled={isStartDisabled} recaptchaToken={this.recaptchaToken} />}/>            
          <Route exact path='/form' render={props => <ProgressBar {...props} unMount={this.unMount} formCompleted={this.formCompleted}/>} />
          <Route exact path='/confirm' component={ShowConfirmation} />
        </Routes>
      </ErrorBoundary> */}
      
      <Footer />
      
      {recaptchaKey !== null &&
        import.meta.env.MODE !== 'development' &&
        import.meta.env.MODE !== 'test' && (
        <GoogleReCaptchaProvider
          reCaptchaKey={recaptchaKey}
          useEnterprise="true"
        >
          <GoogleReCaptcha action="ecmir_verify" onVerify={reCaptchaCallback} />
        </GoogleReCaptchaProvider>
      )}

    </Grid>
  );
}

export default App;